import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { listAssessments } from "@/lib/admin.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/assessments")({
  component: AssessmentsPage,
});

function riskBadge(risk: string) {
  const cls =
    risk === "high"
      ? "bg-destructive/10 text-destructive"
      : risk === "medium"
      ? "bg-amber-100 text-amber-800"
      : "bg-emerald-100 text-emerald-800";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${cls}`}>
      {risk}
    </span>
  );
}

function AssessmentsPage() {
  const fn = useServerFn(listAssessments);
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["admin", "assessments"],
    queryFn: () => fn(),
  });

  if (isPending) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (isError) return <p className="text-sm text-destructive">Failed to load: {(error as Error).message}</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Assessments</h1>
        <p className="text-sm text-muted-foreground">
          Back and neck pain risk assessment submissions. Follow up with patients based on their risk level.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent submissions ({data.length})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Score</th>
                <th className="px-4 py-3">Risk</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No assessments yet.
                  </td>
                </tr>
              ) : (
                data.map((a) => (
                  <tr key={a.id} className="border-t">
                    <td className="px-4 py-3 whitespace-nowrap">
                      {new Date(a.created_at).toLocaleString("en-ZA", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="px-4 py-3 font-medium">{a.full_name}</td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${a.email}`} className="text-primary hover:underline">
                        {a.email}
                      </a>
                    </td>
                    <td className="px-4 py-3">{a.score}</td>
                    <td className="px-4 py-3">{riskBadge(a.risk_level)}</td>
                    <td className="px-4 py-3">
                      <a
                        href={`mailto:${a.email}?subject=${encodeURIComponent(
                          "Your back and neck pain assessment",
                        )}`}
                        className="text-xs font-semibold text-primary hover:underline"
                      >
                        Email
                      </a>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
